// Pure transform mapping the oRPC `extraPackage.list` response into the
// feature's domain model.
//
// The oRPC source (`userExtraPackageSku`) returns a single currency per
// call, so `prices` holds a single entry — see `useExtraPackages` for the
// multi-currency merge that preserves `prices[Currency.Usd]` for analytics.
import type { Currency } from 'pixai-contract/payment/const'
import type { UserExtraPackageSku } from 'src/services/orpc/extraPackage'
import type { ExtraPackage } from './types'

// priceMicros uses micro-units (1/1,000,000) — a currency-agnostic
// convention (cf. Google Money / ISO 4217 minor-unit scaling).
export const MICROS_PER_UNIT = 1_000_000

/**
 * Map a single oRPC SKU to the domain `ExtraPackage` shape. Used by
 * `adaptOrpcSkusToExtraPackages`.
 */
function adaptOrpcSkuToExtraPackage(
  sku: UserExtraPackageSku,
  currentTier: number | undefined,
): ExtraPackage {
  const currency = sku.currency
  const price = Number(sku.priceMicros) / MICROS_PER_UNIT

  return {
    id: sku.id,
    legacyItemId: sku.legacyItemId,
    values: sku.tierBoosts.map(tb => ({
      tier: tb.tier,
      value: tb.value,
      creditsAfterBoosts: tb.creditsAfterBoosts,
      boosts: tb.extraPackageBoosts ?? [],
    })),
    prices: { [currency]: price } as Record<Currency, number>,
    oneTimePurchase: sku.oneTimePurchase,
    monthlyPurchase: sku.monthlyPurchase,
    extraPackageBoosts:
      sku.tierBoosts.find(tb => tb.tier === currentTier)?.extraPackageBoosts ??
      sku.tierBoosts.find(tb => tb.tier === 0)?.extraPackageBoosts,
    promotions: sku.promotions ?? [],
    marketingMetadata: sku.marketingMetadata,
    packageContents: (sku.packageContents ?? []).map(item => ({
      kind: item.kind,
      code: item.code ?? null,
      quantity: item.quantity ?? null,
      metadata: item.metadata ?? null,
    })),
  }
}

/**
 * Convert a list of oRPC SKUs into `ExtraPackage[]`.
 *
 * @param skus Active-currency SKUs returned by `extraPackage.list`.
 * @param currentTier User's current membership tier — drives the
 *   `extraPackageBoosts` field. Pass `undefined` to get tier-0 boosts.
 * @returns Normalized extra packages, or `undefined` if input is missing
 *   or empty (matches the pre-refactor "render nothing" contract).
 */
export function adaptOrpcSkusToExtraPackages(
  skus: readonly UserExtraPackageSku[] | undefined,
  currentTier: number | undefined,
): ExtraPackage[] | undefined {
  if (!skus) return undefined

  return skus.map(sku => adaptOrpcSkuToExtraPackage(sku, currentTier))
}
